/* eslint-disable react/prop-types */
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import { Button, Checkbox, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ATS_INT_STATUSES, ATS_SCREENING, ATS_SOURCES } from "Views/Ats/constants";

const tones = {
  success: { background: "#e8f5e9", color: "#1b5e20", border: "#66bb6a" },
  error: { background: "#ffebee", color: "#b71c1c", border: "#ef5350" },
  info: { background: "#e3f2fd", color: "#0d47a1", border: "#42a5f5" },
  primary: { background: "#ede7f6", color: "#4527a0", border: "#7e57c2" },
  warning: { background: "#fff8e1", color: "#e65100", border: "#ffb74d" },
  neutral: { background: "#fff", color: "#344767", border: "#d2d6da" },
};

const screeningTone = { YES: "success", NO: "error" };
const statusTone = {
  "R1 Scheduled": "info",
  "Telephonic Interview done": "primary",
  "R1 done": "success",
  "R1 Rejected": "error",
  R2: "warning",
};

function CellSelect({ value, options, onChange, tone, ariaLabel, width }) {
  const palette = tones[tone] || tones.neutral;
  return (
    <select
      aria-label={ariaLabel}
      value={value || ""}
      onChange={onChange}
      style={{
        width,
        minHeight: "32px",
        padding: "4px 8px",
        borderRadius: "8px",
        border: `1px solid ${palette.border}`,
        backgroundColor: palette.background,
        color: palette.color,
        fontWeight: 700,
        fontSize: "12px",
        cursor: "pointer",
        outline: "none",
      }}
    >
      <option value="">Select</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

const clip = (value, words) => {
  if (!value) return "-";
  const parts = String(value).split(" ");
  if (parts.length <= words) return value;
  return `${parts.slice(0, words).join(" ")}...`;
};

const CellText = ({ children, title }) => (
  <ArgonTypography variant="caption" color="text" fontWeight="medium" title={title || ""}>
    {children}
  </ArgonTypography>
);

const atsTableData = (
  candidates = [],
  startIndex = 0,
  onView,
  onEdit,
  onDelete,
  onViewResume,
  onViewTemplate,
  onFieldChange,
  selection = null
) => ({
  columns: [
    ...(selection?.enabled
      ? [
          {
            name: "selection",
            label: (
              <Checkbox
                size="small"
                checked={selection.allSelected}
                indeterminate={!selection.allSelected && selection.selectedIds.length > 0}
                onChange={(event) => selection.onToggleAll(event.target.checked)}
                inputProps={{ "aria-label": "Select all candidates on this page" }}
              />
            ),
            align: "center",
            width: "48px",
          },
        ]
      : []),
    { name: "sno", label: "S.N", align: "center", width: "64px" },
    { name: "candidateName", label: "Candidate Name", align: "left", width: "160px" },
    { name: "jobProfile", label: "Job Profile", align: "left", width: "180px" },
    { name: "resume", label: "Resume", align: "center", width: "90px" },
    { name: "preRoundTemplate", label: "Pre Round Template", align: "center", width: "150px" },
    { name: "source", label: "Source", align: "left", width: "170px" },
    { name: "screening", label: "Screening Qualified", align: "center", width: "150px" },
    { name: "intStatus", label: "Int Status", align: "left", width: "210px" },
    { name: "locality", label: "Locality in Bhopal", align: "left", width: "160px" },
    { name: "introRating", label: "Intro Ratings (/10)", align: "center", width: "120px" },
    { name: "callingRemark", label: "Comment", align: "left", width: "220px" },
    { name: "action", label: "Action", align: "center", width: "120px" },
  ],
  rows: candidates.map((candidate, index) => {
    const rating =
      candidate.introRating === 0 || candidate.introRating ? candidate.introRating : null;

    return {
      ...(selection?.enabled
        ? {
            selection: (
              <Checkbox
                size="small"
                checked={selection.selectedIds.includes(candidate._id)}
                onChange={(event) => selection.onToggleOne(candidate._id, event.target.checked)}
                inputProps={{ "aria-label": `Select ${candidate.candidateName}` }}
              />
            ),
          }
        : {}),
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {startIndex + index + 1}
        </ArgonTypography>
      ),
      callingRemark: (
        <CellText title={candidate.callingRemark}>{clip(candidate.callingRemark, 6)}</CellText>
      ),
      source: (
        <CellSelect
          ariaLabel={`Source for ${candidate.candidateName}`}
          value={candidate.source}
          options={ATS_SOURCES}
          tone="neutral"
          width="168px"
          onChange={(event) => onFieldChange(candidate, "source", event.target.value)}
        />
      ),
      screening: (
        <CellSelect
          ariaLabel={`Screening for ${candidate.candidateName}`}
          value={candidate.screeningQualified}
          options={ATS_SCREENING}
          tone={screeningTone[candidate.screeningQualified]}
          width="110px"
          onChange={(event) => onFieldChange(candidate, "screeningQualified", event.target.value)}
        />
      ),
      intStatus: (
        <CellSelect
          ariaLabel={`Interview status for ${candidate.candidateName}`}
          value={candidate.intStatus}
          options={ATS_INT_STATUSES}
          tone={statusTone[candidate.intStatus]}
          width="190px"
          onChange={(event) => onFieldChange(candidate, "intStatus", event.target.value)}
        />
      ),
      candidateName: (
        <ArgonTypography variant="caption" color="dark" fontWeight="bold">
          {candidate.candidateName}
        </ArgonTypography>
      ),
      jobProfile: (
        <CellText title={candidate.jobProfile}>{clip(candidate.jobProfile, 4)}</CellText>
      ),
      resume: candidate.hasResume ? (
        <IconButton
          size="small"
          aria-label={`View resume for ${candidate.candidateName}`}
          title={candidate.resumeFileName || "Resume"}
          onClick={() => onViewResume(candidate)}
          sx={{ p: 0.5 }}
        >
          <VisibilityIcon fontSize="small" color="info" />
        </IconButton>
      ) : (
        <CellText>-</CellText>
      ),
      preRoundTemplate: candidate.hasPreRoundTemplate ? (
        <IconButton
          size="small"
          aria-label={`View pre round template for ${candidate.candidateName}`}
          title={candidate.preRoundTemplateFileName || "Pre Round Template"}
          onClick={() => onViewTemplate(candidate)}
          sx={{ p: 0.5 }}
        >
          <VisibilityIcon fontSize="small" color="info" />
        </IconButton>
      ) : (
        <CellText>-</CellText>
      ),
      locality: <CellText title={candidate.locality}>{clip(candidate.locality, 4)}</CellText>,
      introRating: <CellText>{rating === null ? "-" : rating}</CellText>,
      action: (
        <ArgonBox display="flex" alignItems="center">
          <Button
            variant="text"
            size="large"
            aria-label={`View ${candidate.candidateName}`}
            sx={{ minWidth: "32px", p: 0 }}
            onClick={() => onView(candidate)}
          >
            <VisibilityIcon color="info" />
          </Button>
          <Button
            variant="text"
            size="large"
            aria-label={`Edit ${candidate.candidateName}`}
            sx={{ minWidth: "32px", p: 0 }}
            onClick={() => onEdit(candidate)}
          >
            <DriveFileRenameOutlineIcon color="warning" />
          </Button>
          <Button
            variant="text"
            size="large"
            sx={{ minWidth: "32px", p: 0 }}
            onClick={() => onDelete(candidate)}
          >
            <DeleteIcon color="error" />
          </Button>
        </ArgonBox>
      ),
    };
  }),
});

export default atsTableData;
